import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckIcon, Cross1Icon, Pencil1Icon } from '@radix-ui/react-icons';
import * as React from 'react';
import { IconSize } from '../tokens.js';
/**
 * InlineEdit
 *
 * 1. Click pencil → shows input + check + cancel
 * 2. Click check (or Enter) → spinner, runs onSave, then shows updated text
 * 3. Click cancel (or Escape) → reverts, shows original text
 */
export const InlineEdit = React.forwardRef(({ value, onSave, disabled = false, className, editLabel = 'Edit', minLength = 1 }, ref) => {
    const [editing, setEditing] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [draft, setDraft] = React.useState(value);
    const inputRef = React.useRef(null);
    // Keep draft in sync when value changes externally (e.g. optimistic rollback)
    React.useEffect(() => {
        if (!editing)
            setDraft(value);
    }, [value, editing]);
    const startEdit = () => {
        if (disabled || saving)
            return;
        setDraft(value);
        setEditing(true);
    };
    const commit = async () => {
        const trimmed = draft.trim();
        if (trimmed.length < minLength || trimmed === value) {
            cancel();
            return;
        }
        setSaving(true);
        try {
            await onSave(trimmed);
        }
        finally {
            setSaving(false);
            setEditing(false);
        }
    };
    const cancel = () => {
        setDraft(value);
        setEditing(false);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
        }
    };
    React.useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);
    return (_jsx("span", { className: `inline-edit${className ? ` ${className}` : ''}`, ref: ref, children: editing ? (_jsxs(_Fragment, { children: [_jsx("input", { ref: inputRef, className: "inline-edit__input", value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: handleKeyDown, disabled: saving, "aria-label": editLabel }), _jsx("button", { type: "button", className: "inline-edit__action inline-edit__action--commit inline-edit__trigger button button--ghost button--sm", onMouseDown: (e) => e.preventDefault(), onClick: () => void commit(), disabled: saving, "aria-label": "Save", children: saving ? (_jsx("span", { className: "inline-edit__spinner", "aria-hidden": "true" })) : (_jsx(CheckIcon, { width: IconSize.sm, height: IconSize.sm, "aria-hidden": "true" })) }), _jsx("button", { type: "button", className: "inline-edit__action inline-edit__action--cancel inline-edit__trigger button button--ghost button--sm", onMouseDown: (e) => e.preventDefault(), onClick: cancel, disabled: saving, "aria-label": "Cancel", children: _jsx(Cross1Icon, { width: IconSize.sm, height: IconSize.sm, "aria-hidden": "true" }) })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "inline-edit__display", children: value }), _jsx("button", { type: "button", className: "inline-edit__trigger", onClick: startEdit, disabled: disabled, "aria-label": editLabel, children: _jsx(Pencil1Icon, { width: IconSize.sm, height: IconSize.sm, "aria-hidden": "true" }) })] })) }));
});
InlineEdit.displayName = 'InlineEdit';
//# sourceMappingURL=InlineEdit.js.map